import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';

import { MetricType } from '../../types';
import { ByteThresholdControl } from '../../ByteThresholdControl';

interface ThresholdInputProps {
  name: string;
  value: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  error?: string;
  metricType: MetricType;
  'data-cy'?: string;
}

export function ThresholdInput({
  name,
  value,
  onChange,
  onBlur,
  error,
  metricType,
  'data-cy': dataCy,
}: ThresholdInputProps) {
  const inputId = dataCy || 'threshold-input';

  return (
    <FormControl
      label={getLabel(metricType)}
      inputId={inputId}
      errors={error}
      required
      tooltip={getTooltip(metricType, value)}
    >
      <div className="flex gap-2">
        {metricType === 'bytes' ? (
          <ByteThresholdControl
            value={value}
            onChange={onChange}
            id={inputId}
            name={name}
            onBlur={onBlur}
            className="flex flex-1 gap-2"
            unitClassName="w-1/6"
          />
        ) : (
          <div className="flex-1">
            <Input
              id={inputId}
              name={name}
              type="number"
              value={value}
              onChange={(event) => onChange(event.target.valueAsNumber || 0)}
              onBlur={onBlur}
              placeholder={getPlaceholder(metricType)}
              data-cy={inputId}
              min={metricType === 'percentage' ? 0 : undefined}
              max={metricType === 'percentage' ? 100 : undefined}
            />
          </div>
        )}

        {metricType === 'percentage' && (
          <div className="text-gray-600 flex items-center px-3">%</div>
        )}
        {metricType === 'days' && (
          <div className="text-gray-600 flex items-center px-3">days</div>
        )}
      </div>

      {metricType === 'bytes' && (
        <div className="text-gray-500 mt-1 text-xs">
          Raw value: {value.toLocaleString()} bytes
        </div>
      )}
    </FormControl>
  );
}

function getLabel(metricType: MetricType): string {
  switch (metricType) {
    case 'percentage':
      return 'Threshold (%)';
    case 'days':
      return 'Threshold (days)';
    case 'bytes':
    case 'raw':
    default:
      return 'Threshold';
  }
}

function getTooltip(metricType: MetricType, value: number): string {
  switch (metricType) {
    case 'percentage':
      return 'The percentage threshold for the condition (0-100)';
    case 'bytes':
      return `The threshold value for the condition. Raw value: ${value.toLocaleString()} bytes`;
    case 'days':
      return 'The number of days before expiry to trigger the alert';
    case 'raw':
    default:
      return 'The threshold value for the condition';
  }
}

function getPlaceholder(metricType: MetricType): string {
  switch (metricType) {
    case 'percentage':
      return 'Enter percentage (e.g., 80)';
    case 'bytes':
      return 'Enter value (e.g., 500)';
    case 'days':
      return 'Enter number of days (e.g., 7)';
    case 'raw':
    default:
      return 'Enter threshold';
  }
}
